export class ExpandButtonComponent {
  private static expandButtonComponentInstance: ExpandButtonComponent;
  private static readonly expandIconSelector: string = '[data-expand-icon-class]';
  private static readonly animateInClass: string = '-animateIn';
  private static readonly animateOutClass: string = '-animateOut';

  protected expandButtonSelector: string = '[data-js-expand-button]';

  protected static setExpandIcon(expandButton: HTMLButtonElement, isExpanded: boolean): void {
    const expandIcon = expandButton.querySelector(ExpandButtonComponent.expandIconSelector) as HTMLElement;

    if (!expandIcon) { return; }

    const { expandIconClass } = expandIcon.dataset;

    if (isExpanded) {
      expandIcon.classList.add(expandIconClass);
    } else {
      expandIcon.classList.remove(expandIconClass);
    }
  }

  public static get instance(): ExpandButtonComponent {
    if (!ExpandButtonComponent.expandButtonComponentInstance) {
      ExpandButtonComponent.expandButtonComponentInstance = new ExpandButtonComponent();
    }

    return ExpandButtonComponent.expandButtonComponentInstance;
  }

  private static getExpandedState(expandButton: HTMLElement): boolean {
    return expandButton.getAttribute('aria-expanded') === 'true';
  }

  private static getExpandTarget(expandButton: HTMLElement): HTMLElement {
    const expandTargetId = expandButton.getAttribute('aria-controls');

    return document.getElementById(expandTargetId);
  }

  public init() {
    window.addEventListener('load', this.setupEvents.bind(this));
  }

  protected onAnimateInStart(expandTarget: HTMLElement): void {
    const { animateInClass } = ExpandButtonComponent;

    expandTarget.classList.add(animateInClass);
    expandTarget.hidden = false;
  }

  protected onAnimateInEnd(expandTarget: HTMLElement): void {
    const { animateInClass } = ExpandButtonComponent;

    expandTarget.classList.remove(animateInClass);
  }

  protected onAnimateOutStart(expandTarget: HTMLElement): void {
    const { animateOutClass } = ExpandButtonComponent;

    expandTarget.classList.add(animateOutClass);
  }

  protected onAnimateOutEnd(expandTarget: HTMLElement): void {
    const { animateOutClass } = ExpandButtonComponent;

    expandTarget.hidden = true;
    expandTarget.classList.remove(animateOutClass);
  }

  protected safelyRemoveAnimationResidue(expandTarget: HTMLElement): void {
    const { animateInClass, animateOutClass } = ExpandButtonComponent;

    expandTarget.classList.remove(animateInClass, animateOutClass);
  }

  private setExpandedState(expandButton: HTMLButtonElement, isExpanded: boolean): void {
    const { onAnimateInStart, onAnimateInEnd, onAnimateOutStart, onAnimateOutEnd } = this;
    const expandTarget = ExpandButtonComponent.getExpandTarget(expandButton);

    if (!expandTarget) {
      return;
    }

    expandTarget.getAnimations().forEach((animation) => {
      animation.cancel();
      this.safelyRemoveAnimationResidue(expandTarget);
    });

    expandButton.setAttribute('aria-expanded', isExpanded.toString());
    ExpandButtonComponent.setExpandIcon(expandButton, isExpanded);

    if (isExpanded) {
      if (getComputedStyle(expandTarget).animationDuration) {
        onAnimateInStart(expandTarget);
        expandTarget.onanimationend = animateInEnd;
        expandTarget.onanimationcancel = animateInEnd;
      } else {
        expandTarget.hidden = false;
      }
    } else {
      if (getComputedStyle(expandTarget).animationDuration) {
        onAnimateOutStart(expandTarget);
        expandTarget.onanimationend = animateOutEnd;
        expandTarget.onanimationcancel = animateOutEnd;
      } else {
        expandTarget.hidden = true;
      }
    }

    function animateInEnd(): void {
      onAnimateInEnd(expandTarget);
      expandTarget.removeEventListener('onanimationend', this);
      expandTarget.removeEventListener('onanimationcancel', this);
    }

    function animateOutEnd(): void {
      onAnimateOutEnd(expandTarget);
      expandTarget.removeEventListener('onanimationend', this);
      expandTarget.removeEventListener('onanimationcancel', this);
    }
  }

  private toggleExpandedState(event: MouseEvent): void {
    const expandButton = event.currentTarget as HTMLButtonElement;

    this.setExpandedState(expandButton, !ExpandButtonComponent.getExpandedState(expandButton)) ;
  }

  private setupEvents(): void {
    document.querySelectorAll(this.expandButtonSelector).forEach((expandButton) => {
      expandButton.addEventListener('click', this.toggleExpandedState.bind(this));
    });
  }
}
